import { useId, useState, type FormEvent } from 'react';
import {
  ROLES_RELACION,
  type Party,
  type Relacion,
  type RolRelacion,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { fechaLarga } from '../componentes.js';

interface Props {
  propiedadId: string;
  relaciones: Relacion[];
  partes: Party[];
  puedeGestionar: boolean;
  idioma: Idioma;
  onCambio: () => void;
}

/**
 * Relaciones de la propiedad con personas y empresas.
 *
 * Una relacion no se borra: se FINALIZA con fecha y motivo. Borrarla haria
 * imposible responder de quien era la villa el ano pasado, que es justo lo que
 * hay que poder contestar.
 */
export function PanelRelaciones({
  propiedadId,
  relaciones,
  partes,
  puedeGestionar,
  idioma,
  onCambio,
}: Props) {
  const ids = { party: useId(), rol: useId(), desde: useId(), motivo: useId() };
  const [partyId, setPartyId] = useState('');
  const [rol, setRol] = useState<RolRelacion>('OWNER');
  const [desde, setDesde] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [finalizando, setFinalizando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');

  const vigentes = relaciones.filter((r) => r.hasta === null);
  const historicas = relaciones.filter((r) => r.hasta !== null);

  const conError = async (accion: () => Promise<unknown>) => {
    setError(null);
    try {
      await accion();
      onCambio();
    } catch (fallo: unknown) {
      setError(
        fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
      );
    }
  };

  const abrir = (evento: FormEvent) => {
    evento.preventDefault();
    void conError(async () => {
      await api.abrirRelacion(propiedadId, { partyId, rol, ...(desde ? { desde } : {}) });
      setPartyId('');
      setDesde('');
    });
  };

  const finalizar = (evento: FormEvent) => {
    evento.preventDefault();
    const id = finalizando;
    if (!id) return;
    void conError(async () => {
      await api.finalizarRelacion(id, { motivo });
      setFinalizando(null);
      setMotivo('');
    });
  };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'propiedades.relaciones')}</h2>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {vigentes.length === 0 ? (
        <p>{t(idioma, 'relacion.vacio')}</p>
      ) : (
        <ul>
          {vigentes.map((relacion) => (
            <li key={relacion.id}>
              <strong>{t(idioma, `relacion.${relacion.rol}`)}</strong>: {relacion.partyNombre}{' '}
              <small>({fechaLarga(relacion.desde, idioma)})</small>{' '}
              {puedeGestionar && (
                <button
                  type="button"
                  className="enlace"
                  onClick={() => setFinalizando(relacion.id)}
                >
                  {t(idioma, 'relacion.finalizar')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {finalizando && (
        <form className="formulario" onSubmit={finalizar}>
          <div className="campo">
            <label htmlFor={ids.motivo}>{t(idioma, 'relacion.motivoFin')}</label>
            <input
              id={ids.motivo}
              required
              minLength={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </div>
          <div className="acciones">
            <button type="submit">{t(idioma, 'relacion.finalizar')}</button>
            <button type="button" className="secundario" onClick={() => setFinalizando(null)}>
              {t(idioma, 'app.cancelar')}
            </button>
          </div>
        </form>
      )}

      {historicas.length > 0 && (
        <details>
          <summary>{t(idioma, 'relacion.historicas')}</summary>
          <ul>
            {historicas.map((relacion) => (
              <li key={relacion.id}>
                {t(idioma, `relacion.${relacion.rol}`)}: {relacion.partyNombre} ·{' '}
                {fechaLarga(relacion.desde, idioma)} → {fechaLarga(relacion.hasta, idioma)} ·{' '}
                {relacion.motivoFin}
              </li>
            ))}
          </ul>
        </details>
      )}

      {puedeGestionar && (
        <form className="formulario" onSubmit={abrir}>
          <div className="campo">
            <label htmlFor={ids.party}>{t(idioma, 'party.titulo')}</label>
            <select
              id={ids.party}
              required
              value={partyId}
              onChange={(e) => setPartyId(e.target.value)}
            >
              <option value="">—</option>
              {partes.map((party) => (
                <option key={party.id} value={party.id}>
                  {party.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor={ids.rol}>{t(idioma, 'relacion.rol')}</label>
            <select
              id={ids.rol}
              value={rol}
              onChange={(e) => setRol(e.target.value as RolRelacion)}
            >
              {ROLES_RELACION.map((valor) => (
                <option key={valor} value={valor}>
                  {t(idioma, `relacion.${valor}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor={ids.desde}>{t(idioma, 'relacion.desde')}</label>
            <input
              id={ids.desde}
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <button type="submit">{t(idioma, 'relacion.anadir')}</button>
        </form>
      )}
    </section>
  );
}
